"use client";

/**
 * Image wipe reveal (Venetian recipe 4).
 *
 * Wrapper clip-path expands from a collapsed top-left corner
 * polygon(0 0, 0 0, 0 0, 0 0) to the full rectangle (duration 1,
 * power1.out) while the inner image settles from scale 1.5 to 1
 * (power2.out). Trigger "top 65%", plays once.
 *
 * Usage: <ImageWipe><Image ... /></ImageWipe>. The hidden state is only
 * applied by GSAP after hydration; no-JS and reduced-motion clients see
 * the image static and complete.
 */
import { useRef, type HTMLAttributes } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "./gsap";

export type ImageWipeProps = HTMLAttributes<HTMLDivElement>;

export function ImageWipe({
  children,
  className = "",
  ...rest
}: ImageWipeProps) {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || prefersReducedMotion()) return;
      const media = el.querySelector("img, video");
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 65%",
          once: true,
        },
      });
      tl.fromTo(
        el,
        { clipPath: "polygon(0% 0%, 0% 0%, 0% 0%, 0% 0%)" },
        {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          duration: 1,
          ease: "power1.out",
        },
        0
      );
      if (media) {
        tl.fromTo(
          media,
          { scale: 1.5 },
          { scale: 1, duration: 1, ease: "power2.out" },
          0
        );
      }
    },
    { scope: ref }
  );

  return (
    <div
      ref={ref}
      className={`overflow-hidden silk-img-rounded ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
